import type { ReplayVideo } from './replayVideos';
import type { KcgnSnapshotDeck } from './kcgnSnapshots';
import { LeagueRule, LeagueStatus, LeagueType } from './leagues';
import { Organizer } from './organizers';

// Variable mutable para permitir configuración dinámica
let _apiEndpoint: string | undefined;

// Función para configurar el API endpoint desde las apps
export const setApiEndpoint = (endpoint: string) => {
  _apiEndpoint = endpoint;
};

// App identification for request headers
let _appInfo:
  | { service: string; version: string; commitHash?: string }
  | undefined;

export const setAppInfo = (
  service: string,
  version: string,
  commitHash?: string
) => {
  _appInfo = { service, version, commitHash };
};

export const getAppInfo = () => _appInfo;

// Función getter para obtener el endpoint de la API
export const getApiEndpoint = (): string => {
  if (_apiEndpoint) {
    return _apiEndpoint;
  }

  // Browser fallback: mismo hostname, puerto 5170 (para dev en móvil)
  if (globalThis.window !== undefined && globalThis.location?.hostname) {
    return `http://${globalThis.location.hostname}:5170`;
  }

  return 'http://localhost:5170';
};

// API_ENDPOINT se mantiene por compatibilidad, pero se recomienda usar getApiEndpoint()
// Esta constante se evalúa en el momento de carga del módulo
export const API_ENDPOINT = 'http://localhost:5170';

export type Nullable<T> = T | null;

export type Generic<T = unknown> = {
  [key: string]: T;
};

export type NetworkError = Error & {
  statusCode: number;
  result: { message: string };
};

export type WithData<T> = {
  data: T;
};

export type WithMeta<T> = {
  data: {
    data: T[];
    meta: {
      lastPage: number;
      total: number;
      page: number;
      perPage: number;
    };
  };
};

export type Languages = {
  de: Nullable<string>;
  en: Nullable<string>;
  fr: Nullable<string>;
  it: Nullable<string>;
  sp: Nullable<string>;
  pt: Nullable<string>;
};

export enum LanguageCode {
  English = 'en',
  French = 'fr',
  German = 'de',
  Italian = 'it',
  Spanish = 'sp',
  Portuguese = 'pt',
}

export enum GameKind {
  TCG = 1,
  DuelLinks = 2,
  SpeedDuel = 3,
  DuelingBook = 4,
  MasterDuel = 5,
  YGOPro = 6,
  YGOOmega = 7,
}

export type GameType = {
  id: number;
  name: string;
  slug: Nullable<string>;
  description: Nullable<string>;
};

// Provinces

export type Country = {
  id: number;
  name: string;
};

export type Province = {
  id: number;
  name: string;
};

// Regions (ranking)

export type RankingRegion = {
  id: number;
  name: string;
  slug: string;
};

// Stores

export type OpeningHour = {
  id?: string;
  open: Nullable<string>;
  close: Nullable<string>;
};

export type WeeklySchedule = {
  day: string;
  isClosed: boolean;
  schedule: OpeningHour[];
};

export type DbOpeningHour = Record<string, OpeningHour[]>;

export enum StoreLinkType {
  Facebook = 'Facebook',
  Phone = 'Phone',
  Email = 'Email',
  YouTube = 'YouTube',
  Instagram = 'Instagram',
  Twitch = 'Twitch',
  WhatsApp = 'WhatsApp',
  Website = 'Website',
  Custom = 'Custom',
  Twitter = 'Twitter',
  WhatsAppNumber = 'WhatsAppNumber',
  Kick = 'Kick',
  Discord = 'Discord',
  TikTok = 'TikTok',
}

export type StoreLink = {
  type: StoreLinkType;
  url: string;
  label: Nullable<string>;
  customIcon?: string;
};

export type StoreEmployee = {
  id: number;
  storeId: number;
  userId: number;
  role: UserRole;
  user: User;
};

/**
 * Staff de un organizador que no es una tienda. La sesión trae sólo
 * `id`/`organizerId`/`role`; `user` viene en `getOrganizerEmployees`.
 */
export type OrganizerEmployee = {
  id: number;
  organizerId: number;
  userId: number;
  role: UserRole;
  user: User;
};

export type StorePhoto = {
  key: string;
  uploadedAt: string;
  uploadedBy?: number;
};

export type Store = {
  id: number;
  isOTS: boolean;
  isCommunity: boolean;
  stateId: Province['id'];
  countryId: Country['id'];
  name: string;
  description?: string;
  slug?: string;
  image: string;
  logoUrl: Nullable<string>;
  address: string;
  city: string;
  // Las cuatro son `String?` en Prisma: una tienda puede no tener
  // georreferencia ni código postal cargados.
  latitude: Nullable<string>;
  longitude: Nullable<string>;
  zipCode: Nullable<string>;
  googleMapsUrl: Nullable<string>;
  province: Province;
  country: Country;
  links: StoreLink[];
  openingHours: Nullable<WeeklySchedule[]>;
  recurringTournaments: StoreTournament[];
  employees: StoreEmployee[];
  organizers: Organizer[];
  photos?: string[];
  tags?: string[];
};

export enum LocationType {
  VENUE = 'VENUE',
  STORE = 'STORE',
  PUBLIC_SPACE = 'PUBLIC_SPACE',
  CONVENTION = 'CONVENTION',
  OTHER = 'OTHER',
}

export type OrganizerLocationInfo = {
  organizerId: number;
  organizerName: string;
  favorite: boolean;
};

export type Location = {
  id: number;
  organizerId: Nullable<number>;
  type: LocationType;
  countryId: number;
  stateId: number;
  city: string;
  name: string;
  address: string;
  latitude: Nullable<string>;
  longitude: Nullable<string>;
  zipCode: Nullable<string>;
  phone: Nullable<string>;
  website: Nullable<string>;
  googleMapsUrl?: string;
  country?: Country;
  state?: Province;
  organizer?: Organizer;
  organizers?: OrganizerLocationInfo[];
};

// Users

export enum UserRole {
  Admin = 'ADMIN',
  Player = 'PLAYER',
  Judge = 'JUDGE',
  Organizer = 'ORGANIZER',
  Employee = 'EMPLOYEE',
  Owner = 'OWNER',
}

export type NicknameVisibility = {
  public?: boolean;
  tournaments?: boolean;
  streaming?: boolean;
};

/**
 * Espejo a mano de `type User` de `apps/api/src/schemas/user.graphql`: el
 * codegen genera los tipos del API, no los del SDK.
 *
 * La nullabilidad es la del schema, campo por campo. Antes casi todos los ids
 * de plataforma, `picture`, `city` y los tres booleanos estaban declarados
 * no-nullables mientras el schema los tiene como `String`/`Boolean` a secas, y
 * el `?? ''` defensivo que ya había en varios call sites era la pista de que
 * el tipo mentía. Si cambia el `.graphql`, este bloque cambia con él.
 */
export type User = {
  /** La funda que eligió para videos y streams (ver Sleeves). */
  sleeveId?: Nullable<number>;
  id: number;
  name: string;
  secondName?: Nullable<string>;
  lastName: string;
  secondLastName?: Nullable<string>;
  shortFullName: string;
  longFullName: string;
  nickname?: Nullable<string>;
  nicknameVisibility?: Nullable<NicknameVisibility>;
  email: string;
  roleId: UserRole;
  anonymityMode: 'NONE' | 'INITIAL' | 'ANONYMOUS';

  countryId: Nullable<number>;
  stateId: Nullable<number>;
  city: Nullable<string>;
  homeStoreId: Nullable<number>;
  favoriteCommunityId: Nullable<number>;
  isStore: boolean;
  isKonamiGuest?: boolean;
  hasPlayedTournament: Nullable<boolean>;
  passwordChangedByUser: Nullable<boolean>;

  apiKeys: Nullable<UserApiKey[]>;
  employees: Nullable<StoreEmployee[]>;
  organizerEmployees?: Nullable<
    Pick<OrganizerEmployee, 'id' | 'organizerId' | 'role'>[]
  >;
  /** Organizadores a su nombre (`TournamentOrganizer.userId`); viene en la sesión. */
  tournamentsOrganized?: Nullable<{ id: number }[]>;

  cossyValidated: Nullable<boolean>;
  emailValidated: Nullable<boolean>;
  picture: Nullable<string>;

  cossyId: Nullable<string>;
  masterDuelId: Nullable<string>;
  duelLinksId: Nullable<string>;
  neuronId: Nullable<string>;
  duelingBookId: Nullable<string>;
  challongeId: Nullable<string>;
  topDeckId: Nullable<string>;
  topDeckNick: Nullable<string>;
  limitlessId: Nullable<string>;
  limitlessNick: Nullable<string>;
  discordId: Nullable<string>;

  createdAt: Date;
  updatedAt: Date;
  deletedAt: Nullable<Date>;
  firstLoginAt: Nullable<Date>;
  lastLoginAt: Nullable<Date>;
  lastUsersDownloadAt: Nullable<Date>;

  homeStore: Nullable<Store>;
  favoriteCommunity: Nullable<Store>;
  province: Nullable<Province>;
  tournaments: Nullable<Tournament[]>;
  lastTournamentPlayedAt: Nullable<Date>;
  cossyHistory: Nullable<UserCossyHistory[]>;
  isP?: Nullable<boolean>;
  isJudge?: Nullable<boolean>;
};

/** `type UserCossyHistory` de `user.graphql`. */
export type UserCossyHistory = {
  id: number;
  cossyId: string;
  note: Nullable<string>;
  createdAt: Date;
};

export type UserApiKey = {
  id: number;
  key: string;
  createdAt: Date;
  expiresAt: Nullable<Date>;
};

/**
 * El usuario de una sesión. `isP` y `emailValidated` salieron de acá porque
 * ahora están en `User`, que es donde `user.graphql` los tiene: redeclararlos
 * más angostos hacía que un `User` legítimo no fuera asignable a este tipo.
 */
export type UserWithToken = User & {
  accessToken?: string; // Optional - now using cookie-based auth
};

// Dashboard

export type InventoryDashboard = {
  boxes: Nullable<number>;
  inventory: Nullable<number>;
  decks: Nullable<number>;
};

export type OrganizerDashboard = {
  tournamentsPublished: Nullable<number>;
  tournamentsPending: Nullable<number>;
  tournamentsRunning: Nullable<number>;
  tournamentsFinished: Nullable<number>;
  tournamentsCancelled: Nullable<number>;
  tournamentsNotRunned: Nullable<number>;
  tournamentsUnreported: Nullable<number>;
};

export type TodayDashboard = {
  tournaments: Nullable<number>;
  players: Nullable<number>;
};

// Cache
export type CacheEntry = {
  key: string;
  size: number | null;
  expiresAt: string | null;
  createdAt: string | null;
};

export type CacheInfo = {
  key: string;
  ttl: number;
  description: string;
  entries: CacheEntry[];
  totalSize: number;
  entryCount: number;
};

export type CacheStatus = {
  enabled: boolean;
  caches: CacheInfo[];
  totalSize: number;
};

// Logs

export type LogArchive = {
  date: string;
  key: string;
  size: Nullable<number>;
  uploadedAt: Nullable<string>;
};

// Cards

export type Card = {
  id: number;
  konamiId: number;

  neuronId: number;

  name: string;
  description: string;

  type: string;
  image: Nullable<string>;
  frameType: FrameTypeIcon;

  names: Languages;
  descriptions: Languages;

  sets: CardSet[];

  attribute: Nullable<string>;

  /**
   * El arquetipo que reporta YGOProDeck, por nombre. **Es display, no
   * membresía**: no tiene lo cargado a mano y es uno solo aunque la carta
   * pertenezca a dos. La membresía real está en `archetypes`.
   * Opcional: sólo lo piden las queries que lo necesitan.
   */
  archetype?: Nullable<string>;

  /**
   * Los arquetipos a los que pertenece, de `ArchetypeCard`. Ésta es la
   * membresía real. Opcional: sólo lo piden las queries que lo necesitan.
   */
  archetypes?: { id: number; name: string }[];

  data: {
    atk: number;
    def: number;
    race: string;
    level: number;
  };

  genesysPoints?: number;
  previousGenesysPoints?: number;

  /** `false` si el `konamiId` es inventado o no se confirmó contra una fuente. */
  konamiIdValidated?: boolean;
  completed?: boolean;
  imagesCompleted?: boolean;
};

/**
 * Lo que un admin corrige a mano de una carta. Lo que no viene no se toca;
 * `attribute` y `archetype` en `null` vacían la columna, y `data` sólo pisa
 * las claves que trae.
 */
export type UpdateCardInput = {
  completed?: boolean;
  imagesCompleted?: boolean;
  konamiId?: number;
  konamiIdValidated?: boolean;
  /** BigInt en el API: va como string para no perder precisión. */
  neuronId?: string;
  name?: string;
  type?: string;
  frameType?: string;
  attribute?: Nullable<string>;
  archetype?: Nullable<string>;
  data?: {
    race?: string;
    level?: number;
    atk?: string;
    def?: string;
  };
};

export enum CardState {
  Mint = 'MINT',
  NearMint = 'NEAR_MINT',
  Excellent = 'EXCELLENT',
  Good = 'GOOD',
  LightPlayed = 'LIGHT_PLAYED',
  Played = 'PLAYED',
  Poor = 'POOR',
}

export enum CardLanguage {
  English = 'ENGLISH',
  German = 'GERMAN',
  Italian = 'ITALIAN',
  French = 'FRENCH',
  Spanish = 'SPANISH',
  Portuguese = 'PORTUGUESE',
  Korean = 'KOREAN',
  Japanese = 'JAPANESE',
  AsianEnglish = 'ASIAN_ENGLISH',
  Chinese = 'CHINESE',
}

export enum CardTypeIcon {
  Aqua = 'aqua',
  BeastWarrior = 'beast-warrior',
  Beast = 'beast',
  Dinosaur = 'dinosaur',
  Dragon = 'dragon',
  Fairy = 'fairy',
  Fiend = 'fiend',
  Fish = 'fish',
  Insect = 'insect',
  Machine = 'machine',
  Plant = 'plant',
  Psychic = 'psychic',
  Pyro = 'pyro',
  Reptile = 'reptile',
  Rock = 'rock',
  SeaSerpent = 'sea-serpent',
  Spellcaster = 'spellcaster',
  Thunder = 'thunder',
  Warrior = 'warrior',
  WingedBeast = 'winged-beast',
  Zombie = 'zombie',
  Illusion = 'illusion',
  Cyberse = 'cyberse',
  Wyrm = 'wyrm',
  QuickPlay = 'quick-play',
  Continuous = 'continuous',
  Ritual = 'ritual',
  Equip = 'equip',
  Normal = 'normal',
  Field = 'field',
  Counter = 'counter',
}

export enum FrameTypeIcon {
  Effect = 'effect',
  Fusion = 'fusion',
  Link = 'link',
  Normal = 'normal',
  Ritual = 'ritual',
  Spell = 'spell',
  Synchro = 'synchro',
  Trap = 'trap',
  Xyz = 'xyz',
}

export enum CardAttribute {
  Earth = 'earth',
  Fire = 'fire',
  Wind = 'wind',
  Light = 'light',
  Dark = 'dark',
  Water = 'water',
  Trap = 'trap',
  Spell = 'spell',
}

// Boxes

export enum BoxType {
  Deckbox = 'DECKBOX',
  Folder = 'FOLDER',
  Box = 'BOX',
  Tin = 'TIN',
  Other = 'OTHER',
}

export type Box = {
  id: number;
  name: string;
  description: string;
  type: BoxType;
  customType?: string;
  capacity: number;
  isDefault?: boolean;

  inventory: Inventory[];

  createdAt: string;
  updatedAt: string;
};

// Inventories

// Sale Configuration
export enum InventoryPriceType {
  FIXED_ARS = 'FIXED_ARS',
  FIXED_USD = 'FIXED_USD',
  MARKET_AVERAGE = 'MARKET_AVERAGE',
  MARKET_ABOVE = 'MARKET_ABOVE',
  MARKET_BELOW = 'MARKET_BELOW',
  ON_REQUEST = 'ON_REQUEST',
}

export type DollarRate = {
  blue: number;
  oficial: number;
  euroBlue?: number;
  euroOficial?: number;
  fetchedAt: string;
};

export type MarketPriceInfo = {
  min: number | null;
  max: number | null;
  average: number | null;
  count: number;
};

export type ConfigureSaleInput = {
  priceType: InventoryPriceType;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  marketPricePercentage?: number;
  isNegotiable: boolean;
  quantity?: number;
};

export type QuickSellerSetupInput = {
  paymentMethod: 'transfer' | 'mercadopago' | 'cash';
  bankName?: string;
  cbuOrCvu?: string;
  alias?: string;
  mercadoPagoAlias?: string;
  deliveryOption: 'shipping' | 'pickup';
  shippingMethod?: string;
  shippingPrice?: number;
};

export type Inventory = {
  id: number;
  konamiId: Nullable<number>;
  cardSetId: Nullable<number>;
  rarity: Nullable<string>;
  // Nullable de verdad: `CardLanguage?` en Prisma y `language: String` en el
  // schema. Tipado como `string` a secas, TypeScript no veía que las celdas que
  // lo pintan podían recibir null y romper la lista entera en el render.
  language: string | null;
  quantity: number;
  state: CardState;

  salable?: boolean;
  salePrice?: number;
  priceType?: InventoryPriceType;
  salePriceUSD?: number;
  customDollarRate?: number;
  marketPricePercentage?: number;
  isNegotiable?: boolean;

  box: Box;

  /**
   * Nullable: las filas de producto —boosters, cajas selladas— no son una
   * carta y no tienen `cardSet`. La colección ya las filtraba a mano; ahora el
   * tipo lo dice.
   */
  cardSet: Nullable<
    Pick<
      CardSet,
      | 'id'
      | 'code'
      | 'card'
      | 'rarity'
      | 'image'
      | 'lastCardMarket'
      | 'lastCardTrader'
      | 'lastTcgPlayer'
    > & {
      set?: Pick<Set, 'konamiName'>;
    }
  >;
};

// Listings

export type ListingStatus = 'ACTIVE' | 'PAUSED' | 'SOLD' | 'CANCELLED';

export type ListingEventType =
  | 'CREATED'
  | 'UPDATED'
  | 'ACTIVATED'
  | 'PAUSED'
  | 'SOLD'
  | 'CANCELLED'
  | 'DELETED';

export type ListingEvent = {
  id: number;
  listingId: number;
  type: ListingEventType;
  message?: string;
  metadata?: Record<string, unknown>;
  user: Pick<User, 'id' | 'name' | 'lastName'>;
  createdAt: string;
};

export type Listing = {
  id: number;
  quantity: number;
  priceType: InventoryPriceType;
  salePrice: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  marketPricePercentage?: number;
  isNegotiable: boolean;
  status: ListingStatus;
  // Última vez que el sistema recalculó el precio (sólo precios automáticos).
  priceUpdatedAt?: string | null;
  inventory: Inventory;
  events?: ListingEvent[];
  createdAt: string;
  updatedAt: string;
};

export type CreateListingInput = {
  inventoryId: number;
  quantity: number;
  priceType: InventoryPriceType;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  marketPricePercentage?: number;
  isNegotiable: boolean;
};

export type UpdateListingInput = {
  quantity?: number;
  priceType?: InventoryPriceType;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  marketPricePercentage?: number;
  isNegotiable?: boolean;
};

export type CreateDirectListingInput = {
  konamiId: number;
  cardSetId: number;
  rarity: string;
  language?: string;
  state?: string;
  quantity: number;
  priceType: InventoryPriceType;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  marketPricePercentage?: number;
  isNegotiable: boolean;
};

// Wishlist types
export type Wishlist = {
  id: number;
  name: string;
  description?: string;
  shareHash?: string;
  isPublic: boolean;
  cards: WishlistCard[];
  cardCount: number;
  createdAt: string;
  updatedAt: string;
};

// Edicion elegida dentro de una carta buscada: el mismo CardSet del catalogo
// recortado a lo que la wishlist pide en su fragmento.
export type WishlistCardSet = Pick<
  CardSet,
  | 'id'
  | 'code'
  | 'rarity'
  | 'label'
  | 'image'
  | 'lastCardMarket'
  | 'lastCardTrader'
  | 'lastTcgPlayer'
> & {
  set?: Nullable<Pick<Set, 'id' | 'konamiName' | 'code'>>;
};

export type WishlistCard = {
  id: number;
  konamiId: number;
  languages: string[];
  minState: string | null;
  quantity: number;
  card: Card;
  // Primera edicion elegida, null cuando sirve cualquiera.
  cardSet: Nullable<WishlistCardSet>;
  // Todas las ediciones elegidas. Vacio = cualquier edicion.
  cardSets: WishlistCardSet[];
  createdAt: string;
};

// Set recortado a lo que necesita un selector.
export type SetOption = {
  id: number;
  konamiName: string;
  code: string;
  releaseDate: Nullable<string>;
};

// Ediciones de un set (incluidas las de sus promos) para elegir en lote.
export type SetCardSets = {
  id: number;
  konamiName: string;
  code: string;
  allCardSets: Array<{
    id: number;
    code: string;
    rarity: string;
    label: string;
    card: { konamiId: number };
  }>;
};

export type WishlistCardEditionsInput = {
  cardId: number;
  cardSetIds: number[];
};

export type CreateWishlistInput = {
  name: string;
  description?: string;
};

export type UpdateWishlistInput = {
  name?: string;
  description?: string;
};

export type AddWishlistCardInput = {
  wishlistId: number;
  konamiId: number;
  cardSetId?: number;
  languages?: string[];
  minState?: string;
};

export type UpdateWishlistCardInput = {
  cardSetId?: number;
  cardSetIds?: number[];
  languages?: string[];
  minState?: string | null;
  quantity?: number;
};

export type ImportRow = [
  number, // quantity
  string, // name
  string, // expansion
  string, // language
  number, // number
  string, // code
  string, // rarity
  number, // konamiId
  string, // reason (optional, for errors)
];

export type ImportCardInfo = {
  name: string;
  code: string;
  rarity: string;
  language: string;
  quantity?: number;
  oldQuantity?: number;
  newQuantity?: number;
};

export type ImportCard = {
  konamiId: number;
  language: string;
  marketPrice: number;
  price: number;
  quantity: number;
  userId: number;
  warehouseId: number;
  setCard: ImportRow;
  reason: string;
  // Optional fields returned by backend for error display
  name?: string;
  code?: string;
  cardSetCode?: string;
  rarity?: string;
  cardSet?: {
    code: string;
    card: { name: string };
  };
  cardInfo?: ImportCardInfo;
};

// Sets

export type CardSet = {
  id: number;
  konamiId: number;
  cardId: number;
  code: string;
  rarity: string;
  label: string;
  // Rasgos de la carta física. Opcionales: no todas las queries los piden.
  overframe?: boolean;
  alternateArt?: boolean;
  image?: Nullable<string>;
  cardTraderImage?: Nullable<string>;
  cardMarketImage?: Nullable<string>;
  tcgPlayerImage?: Nullable<string>;

  card: Card;
  set: Set;
  inventory: Inventory[];
  codes: Languages;

  // Provider mapping
  tcgPlayerId?: Nullable<number>;
  cardTraderId?: Nullable<number>;
  cardMarketId?: Nullable<number>;

  // Latest cached prices (UI-scaled). cardMarket/cardTrader = EUR, tcgPlayer = USD
  lastCardMarket?: Nullable<number>;
  lastCardTrader?: Nullable<number>;
  lastTcgPlayer?: Nullable<number>;
};

export type KonamiDbSet = {
  setId: Nullable<number>;
  konamiName: Nullable<string>;
  url: Nullable<string>;
  releaseDate: Nullable<string>;
  neuronId: Nullable<number>;
};

export type ReleaseDate = {
  region: Region;
  date: Date;
};

export type Set = {
  id: number;
  konamiName: string;
  konamiNames: Languages;

  completed: boolean;
  rarities: Nullable<string[]>;
  neuronId: number;

  code: string;
  codes: Languages;

  image: Nullable<string>;
  numberOfCards: number;
  collectionSize: number;

  isOldSet: boolean;
  releaseDate: Nullable<Date>;
  correlativeCode: boolean;
  releaseDates: Nullable<ReleaseDate[]>;

  urls?: {
    konamiDb?: Nullable<string>;
    yugipedia?: Nullable<string>;
    ygoProDeck?: Nullable<string>;
    ygoWikia?: Nullable<string>;
    ygoFandom?: Nullable<string>;
    cardtrader?: Nullable<string>;
    cardmarket?: Nullable<string>;
    tcgcsv?: Nullable<string>;
  };

  cardSet: Nullable<CardSet[]>;

  parentSetId?: Nullable<number>;
  parent?: Nullable<Pick<Set, 'id' | 'konamiName' | 'code'>>;
  children?: Pick<Set, 'id' | 'konamiName' | 'code'>[];

  neuronIds: Nullable<Languages>;

  // Provider mapping
  cardTraderId?: Nullable<number>;
  cardMarketId?: Nullable<number>;
  tcgCSVId?: Nullable<number>;

  // Alert fields
  hasUnknownRarities?: boolean;
  unknownRaritiesCount?: number;
  hasCardGaps?: boolean;
  cardGapsCount?: number;
  hasMissingCardTraderIds?: boolean;
  missingCardTraderIdsCount?: number;
  hasMissingCardMarketIds?: boolean;
  missingCardMarketIdsCount?: number;
  hasMissingNeuronIds?: boolean;
  missingNeuronIdsCount?: number;
  hasMissingSetInfo?: boolean;
  missingSetInfoDetails?: string[];
  hasMissingImages?: boolean;
  missingImagesCount?: number;
  selectedImagesCount?: number;
  missingCmS3Count?: number;
  missingCtS3Count?: number;
  hasAllCardImages?: boolean;
  hasAllCardImagesWithProviders?: boolean;
  cardsMissingAnyImageCount?: number;
  cardsMissingFullImageCount?: number;
};

// Set Review / Audit types
export type AuditProvider = 'CARDTRADER' | 'CARDMARKET' | 'TCGPLAYER';

export type AuditCategory =
  | 'MISSING'
  | 'AMBIGUOUS'
  | 'CURRENTLY_WRONG'
  | 'DUPLICATE_PROVIDER_ID'
  | 'WRONG_GAME'
  | 'OK';

export type CardSetAuditIssue = {
  provider: AuditProvider;
  category: AuditCategory;
  ambiguousCandidates?: Nullable<number[]>;
  /** Labels de esos candidatos, en el mismo orden: qué versiones publica el
   * provider y, por diferencia, cuáles nos faltan crear. */
  ambiguousLabels?: Nullable<string[]>;
  expectedId?: Nullable<number>;
  message?: Nullable<string>;
};

export type SetReviewRow = {
  cardSet: CardSet;
  konamiImage: Nullable<string>;
  issues: CardSetAuditIssue[];
  isComplete: boolean;
};

export type SetReviewSummary = {
  total: number;
  complete: number;
  withIssues: number;
};

export type SetReviewSetInfo = {
  id: number;
  code: string;
  konamiName: Nullable<string>;
  cardTraderId: Nullable<number>;
  cardMarketId: Nullable<number>;
  tcgCSVId: Nullable<number>;
};

export type SetReview = {
  set: SetReviewSetInfo;
  rows: SetReviewRow[];
  summary: SetReviewSummary;
};

// Credits
export enum UserCreditMoveType {
  Deposit = 'DEPOSIT',
  Withdraw = 'WITHDRAW',
  Transfer = 'TRANSFER',
  Payment = 'PAYMENT',
  Receipt = 'RECEIPT',
  Other = 'OTHER',
  Prize = 'PRIZE',
}

export type UserCreditMove = {
  id: number;
  accountId: number;
  userId: number;
  amount: number;
  type: UserCreditMoveType;
  note: Nullable<string>;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Nullable<Date>;
};

export type Credit = {
  id: number;
  userId: User['id'];
  storeId: Store['id'];
  balance: number;

  user: Pick<User, 'id' | 'name' | 'lastName' | 'cossyId'>;
  store: Pick<Store, 'name'>;

  moves: UserCreditMove[];

  createdAt: Date;
  updatedAt: Date;
  deletedAt: Nullable<Date>;
};

// Events

export type Event = {
  id: number;
  storeId: Store['id'];
  title: string;
  location: string;
  description: string;
  dateFrom: Date;
  dateTo: Date;
  fbuid?: string;
  image?: string;
};

// Decklists

export type DecklistCard = {
  code: number;
  name: string;
  quantity: number;
  available: boolean;
  inventory: Inventory[];
};

export type CardList = {
  code: number;
  name: string;
  quantity: number;
  available: boolean;
};

export type Decklist = {
  id: number;
  userId: User['id'];
  gameTypeId: GameType['id'];

  name: string;
  createdAt: string;
  updatedAt: string;

  decks: Nullable<{
    main: DecklistCard[];
    side: DecklistCard[];
    extra: DecklistCard[];
  }>;
  gameType: GameType;
  user: User;
};

// Judges
export type Judge = {
  id: number;
  dc: Nullable<number>;
  pc: number;
  rc: number;
  user?: User;
  userId?: User['id'];
};

// Tournament Judges

export enum JudgePosition {
  HeadJudge = 'HEAD_JUDGE',
  FloorJudge = 'FLOOR_JUDGE',
  Scorekeeper = 'SCOREKEEPER',
  TeamLead = 'TEAM_LEAD',
  AssistantHeadJudge = 'ASSISTANT_HEAD_JUDGE',
  PublicsHeadJudge = 'PUBLICS_HEAD_JUDGE',
}

// Nombres de los puestos, en el orden en que se listan (de mayor a menor).
export const JUDGE_POSITION_LABELS: Record<JudgePosition, string> = {
  [JudgePosition.HeadJudge]: 'Head Judge',
  [JudgePosition.AssistantHeadJudge]: 'Assistant Head Judge',
  [JudgePosition.PublicsHeadJudge]: 'Publics Head Judge',
  [JudgePosition.TeamLead]: 'Team Lead',
  [JudgePosition.FloorJudge]: 'Floor Judge',
  [JudgePosition.Scorekeeper]: 'Scorekeeper',
};

export const JUDGE_POSITION_ORDER: JudgePosition[] = Object.keys(
  JUDGE_POSITION_LABELS
) as JudgePosition[];

// "Team Lead · Deck Checks"; el resto de los puestos, sólo su nombre.
export const judgePositionLabel = (
  position: JudgePosition | string,
  description?: string | null
) => {
  const label = JUDGE_POSITION_LABELS[position as JudgePosition] ?? position;
  return description ? `${label} · ${description}` : label;
};

export enum TournamentJudgeStatus {
  INVITED = 'INVITED',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
}

export type TournamentJudge = {
  id: number;
  userId: number;
  tournamentId: number;
  position: JudgePosition;
  description?: string | null;
  status: TournamentJudgeStatus;
  user: User;
  judge?: Judge;
  createdAt: Date;
  updatedAt: Date;
};

export enum DeckCheckResult {
  OK = 'OK',
  MINOR_ERROR = 'MINOR_ERROR',
  MAJOR_ERROR = 'MAJOR_ERROR',
}

export enum DeckCheckStatus {
  NONE = 'NONE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export type DeckCheck = {
  id: number;
  tournamentId: number;
  matchId: number;
  playerId: number;
  judgeId: number;
  round: number;
  result: DeckCheckResult;
  notes: Nullable<string>;
  penaltyId: Nullable<number>;
  startedAt: Date;
  completedAt: Nullable<Date>;
  player?: Pick<User, 'id' | 'name' | 'lastName'> | null;
  judge?: Pick<User, 'id' | 'name' | 'lastName'>;
  match?: { id: number; round: number; tableNumber: number } | null;
};

export enum JudgeCallStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  ESCALATED = 'ESCALATED',
  RESOLVED = 'RESOLVED',
}

export enum JudgeCallPriority {
  NORMAL = 'NORMAL',
  URGENT = 'URGENT',
}

export type JudgeCall = {
  id: number;
  tournamentId: number;
  matchId: Nullable<number>;
  tableNumber: Nullable<number>;
  callerId: number;
  assignedJudgeId: Nullable<number>;
  status: JudgeCallStatus;
  priority: JudgeCallPriority;
  description: Nullable<string>;
  resolution: Nullable<string>;
  createdAt: Date;
  resolvedAt: Nullable<Date>;
  caller?: Pick<User, 'id' | 'name' | 'lastName'>;
  assignedJudge?: Pick<User, 'id' | 'name' | 'lastName'> | null;
  match?: { id: number; round: number; tableNumber: number } | null;
};

export enum AvatarFrame {
  DEFAULT = 'DEFAULT',
  BRONZE = 'BRONZE',
  SILVER = 'SILVER',
  GOLD = 'GOLD',
  PLATINUM = 'PLATINUM',
  DIAMOND = 'DIAMOND',
  LEGENDARY = 'LEGENDARY',
}

export type UserTitle = {
  id: number;
  userId: number;
  title: string;
  isActive: boolean;
  unlockedAt: Date;
};

export type GamificationProfile = {
  level: number;
  totalXp: number;
  activeTitle: Nullable<string>;
  activeAvatarFrame: AvatarFrame;
  unlockedTitles: string[];
  unlockedFrames: AvatarFrame[];
};

export enum XPSource {
  TOURNAMENT_PARTICIPATION = 'TOURNAMENT_PARTICIPATION',
  MATCH_WIN = 'MATCH_WIN',
  TOURNAMENT_WIN = 'TOURNAMENT_WIN',
  TOURNAMENT_TOP4 = 'TOURNAMENT_TOP4',
  MARKETPLACE_LIST = 'MARKETPLACE_LIST',
  MARKETPLACE_SALE = 'MARKETPLACE_SALE',
  MARKETPLACE_BUY = 'MARKETPLACE_BUY',
  COLLECTION_ADD = 'COLLECTION_ADD',
  COLLECTION_SET_COMPLETE = 'COLLECTION_SET_COMPLETE',
  DAILY_LOGIN = 'DAILY_LOGIN',
  DAILY_MISSION = 'DAILY_MISSION',
  WEEKLY_MISSION = 'WEEKLY_MISSION',
  STREAK_7 = 'STREAK_7',
  STREAK_30 = 'STREAK_30',
  ACHIEVEMENT = 'ACHIEVEMENT',
}

export type XPSummary = {
  userId: number;
  totalXp: number;
  level: number;
  xpIntoLevel: number;
  xpForNextLevel: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityAt: Nullable<Date>;
};

export type XPTransaction = {
  id: number;
  userId: number;
  amount: number;
  source: XPSource;
  referenceId: Nullable<string>;
  createdAt: Date;
};

export type XPLeaderboardEntry = {
  userId: number;
  totalXp: number;
  level: number;
  periodXp: Nullable<number>;
  user: Pick<User, 'id' | 'name' | 'lastName'>;
};

export type DailyLoginResult = {
  awarded: number;
  totalXp: number;
  previousLevel: number;
  newLevel: number;
  leveledUp: boolean;
  alreadyClaimedToday: boolean;
};

export enum AchievementCategory {
  TOURNAMENT = 'TOURNAMENT',
  MARKETPLACE = 'MARKETPLACE',
  COLLECTION = 'COLLECTION',
  COMMUNITY = 'COMMUNITY',
  SPECIAL = 'SPECIAL',
}

export type Achievement = {
  id: number;
  key: string;
  name: string;
  description: string;
  category: AchievementCategory;
  iconUrl: Nullable<string>;
  xpReward: number;
  isHidden: boolean;
  requirement: unknown;
  createdAt: Date;
};

export type UserAchievement = {
  id: number;
  userId: number;
  achievementId: number;
  unlockedAt: Date;
  achievement: Achievement;
};

export enum MissionType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
}

export type Mission = {
  id: number;
  key: string;
  name: string;
  description: string;
  type: MissionType;
  xpReward: number;
  requirement: unknown;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type UserMission = {
  id: number;
  userId: number;
  missionId: number;
  progress: number;
  isCompleted: boolean;
  assignedAt: Date;
  completedAt: Nullable<Date>;
  mission: Mission;
};

export type UpdateMissionProgressEntry = {
  userMissionId: number;
  missionKey: string;
  progress: number;
  target: number;
  completed: boolean;
  xpAwarded: number;
};

export type UpdateMissionProgressResult = {
  updated: UpdateMissionProgressEntry[];
};

export type StreakResult = {
  currentStreak: number;
  longestStreak: number;
  streakBumped: boolean;
  bonusXP: number;
};

export enum PenaltyType {
  CAUTION = 'CAUTION',
  WARNING = 'WARNING',
  GAME_LOSS = 'GAME_LOSS',
  MATCH_LOSS = 'MATCH_LOSS',
  DISQUALIFICATION = 'DISQUALIFICATION',
}

export enum PenaltyCategory {
  PROCEDURAL = 'PROCEDURAL',
  UNSPORTING_CONDUCT = 'UNSPORTING_CONDUCT',
  TARDINESS = 'TARDINESS',
  DECK_ERROR = 'DECK_ERROR',
  DRAWING_ERROR = 'DRAWING_ERROR',
  SLOW_PLAY = 'SLOW_PLAY',
  OTHER = 'OTHER',
}

export type Penalty = {
  id: number;
  tournamentId: number;
  // Sin jugador del sistema (lo escribió el juez a mano) va playerName.
  playerId: Nullable<number>;
  playerName: Nullable<string>;
  judgeId: number;
  matchId: Nullable<number>;
  round: number;
  tableNumber: Nullable<number>;
  type: PenaltyType;
  category: PenaltyCategory;
  description: string;
  // PENDING: anotada al resolver una llamada; falta completarla.
  status?: 'PENDING' | 'APPLIED';
  judgeCallId?: Nullable<number>;
  createdAt: Date;
  player?: Pick<User, 'id' | 'name' | 'lastName'> | null;
  judge?: Pick<User, 'id' | 'name' | 'lastName'>;
  match?: { id: number; round: number; tableNumber: number } | null;
};

export type MyPenalty = {
  id: number;
  tournamentId: number;
  round: number;
  type: PenaltyType;
  category: PenaltyCategory;
  description: string;
  judgeName: Nullable<string>;
  createdAt: Date;
};

export type MatchNote = {
  id: number;
  tournamentId: number;
  // Null en las notas del torneo; ronda y mesa pueden venir igual (cargadas a mano).
  matchId: Nullable<number>;
  round: Nullable<number>;
  tableNumber: Nullable<number>;
  judgeId: number;
  content: string;
  createdAt: Date;
  judge?: Pick<User, 'id' | 'name' | 'lastName'>;
  match?: { id: number; round: number; tableNumber: number } | null;
};

// Tournaments

export type TournamentTeam = {
  id: number;
  name: string;
  tournamentId: number;
  position?: number;
  wins?: number;
  losses?: number;
  draws?: number;
  points?: number;
  dropRound?: number;
  players: TournamentTeamPlayer[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
};

export type TournamentTeamPlayer = {
  id: number;
  teamId: number;
  userId: number;
  position: number;
  deckName?: string;
  user: Pick<User, 'id' | 'name' | 'lastName' | 'cossyId'> &
    Partial<Pick<User, 'duelingBookId'>>;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
};

export type TournamentTeamMatch = {
  id: number;
  round: number;
  tableNumber: number;
  tournamentId: number;
  team1Id: Nullable<number>;
  team2Id: Nullable<number>;
  winnerTeamId?: number;
  status: TournamentMatchStatus;
  team1?: TournamentTeam;
  team2?: TournamentTeam;
  winnerTeam?: TournamentTeam;
  individualMatches?: TournamentMatch[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
};

export type TournamentPlayer = {
  id: number;
  status: TournamentPlayerStatus;
  dropRound: Nullable<number>;
  invitedByOrganizer?: Nullable<boolean>;
  user: Pick<
    User,
    | 'id'
    | 'name'
    | 'secondName'
    | 'lastName'
    | 'secondLastName'
    | 'cossyId'
    | 'email'
    | 'masterDuelId'
    | 'duelLinksId'
    | 'neuronId'
    | 'cossyValidated'
    | 'anonymityMode'
    | 'nickname'
    | 'nicknameVisibility'
  > &
    // Opcional: los jugadores que arman los imports (KTS, TopDeck…) no lo traen.
    Partial<Pick<User, 'duelingBookId'>>;
  deckName: Nullable<string>;
  metaDeckId?: Nullable<number>;
  /** Funda en este torneo: pisa la del torneo y la del jugador (ver Sleeves). */
  sleeveId?: Nullable<number>;
  deckImage?: Nullable<string>;
  fixedTable?: Nullable<number>;
  results: TournamentResult[];
};

export enum TournamentType {
  Swiss = 'SWISS',
  SingleElimination = 'SINGLE_ELIMINATION',
  DoubleElimination = 'DOUBLE_ELIMINATION',
  Draft = 'DRAFT',
  RoundRobin = 'ROUND_ROBIN',
}

export enum BracketSide {
  Winners = 'WINNERS',
  Losers = 'LOSERS',
  GrandFinal = 'GRAND_FINAL',
}

export enum TournamentStatus {
  Active = 'ACTIVE',
  Started = 'STARTED',
  Closed = 'CLOSED',
  Cancelled = 'CANCELLED',
  Finished = 'FINISHED',
  Unreported = 'UNREPORTED',
  NotRunned = 'NOT_RUNNED',
}

// UNLISTED y PRIVATE no salen en el calendario ni en anuncios. UNLISTED deja
// la ficha abierta con el link; PRIVATE sólo para el organizador e inscriptos.
export enum TournamentVisibility {
  Public = 'PUBLIC',
  Unlisted = 'UNLISTED',
  Private = 'PRIVATE',
}

export enum TournamentAction {
  Delete = 'DELETE',
  NotRunned = 'NOT_RUNNED',
  Cancel = 'CANCEL',
}

export enum TournamentPlayerStatus {
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Rejected = 'REJECTED',
  InWaitingList = 'IN_WAITING_LIST',
  Banned = 'BANNED',
  Dropped = 'DROPPED',
}

export type TournamentOrganizerRelation = {
  id: number;
  tournamentId: number;
  organizerId: number;
  isPrimary: boolean;
  organizer: Organizer;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
};

export type Tournament = {
  id: number;
  title: string;
  platformId: number;
  platform?: Nullable<{ id: number; slug: Nullable<string> }>;
  gameTypeId: GameType['id'];
  formatId?: number;
  swissId?: number;
  /** Public event: el main event (YCS) del que cuelga. */
  mainEventId?: Nullable<number>;
  publicEventPrizesDeliveredAt?: Nullable<string>;
  organizerId?: number;
  /** Banlist que rige el torneo. `null` = ninguna elegida. */
  banlistId?: Nullable<number>;
  banlist?: Nullable<Banlist>;
  /**
   * La que rige de verdad: la propia, o la vigente global del formato (Konami,
   * o la cargada para un formato retro). Sólo nombre y logo.
   */
  effectiveBanlist?: Nullable<TournamentBanlist>;
  konamiId?: string;
  totalPlayers: Nullable<number>;

  store: Pick<Store, 'id' | 'name'>;

  withTop: number;
  /**
   * Eliminación directa (o el top de un suizo): la final se juega con una mesa
   * más, la de los perdedores de semis, por el 3er y 4to puesto.
   */
  thirdPlaceMatch?: boolean;
  /** Double elimination: revancha si gana la gran final quien viene de losers. */
  grandFinalReset?: boolean;
  /** Suizo con top: con qué llave se juega el top. */
  topType?: TournamentType;
  /** El torneo pide que cada jugador entregue su lista. */
  withDecklist: boolean;
  tournamentType: TournamentType;

  discordChannelId: Nullable<string>;
  /** Canal donde el jugador entrega su lista (lo crea el worker). */
  discordDecklistChannelId?: Nullable<string>;
  /** Canal privado de jueces con el log de cada entrega. */
  discordDecklistLogChannelId?: Nullable<string>;
  /** Torneo de Dueling Book del que el worker trae mesas y resultados. */
  duelingBookTourneyId?: Nullable<string>;
  /** Se juega en Dueling Book, el torneo o (en un top) su suizo. Ver `isDuelingBook`. */
  playedOnDuelingBook?: boolean;
  /** Graba en video los replays de sus mesas. */
  recordReplays?: boolean;
  /** Tope por espera (s) del video del replay; el resto se acelera. */
  replayThinkingCapSeconds?: number;
  replayHandsPanel?: boolean;
  sleeveId?: Nullable<number>;
  /** Playmat del campo en los videos de replays (ver Playmats). */
  playmatId?: Nullable<number>;
  challongeId: Nullable<string>;
  topdeckId: Nullable<string>;
  limitlessId?: Nullable<string>;
  ktsFilePath?: string;
  locationId: Nullable<number>;

  resultsValidated: boolean;

  description: Nullable<string>;
  image: Nullable<string>;
  rounds: number;

  ogImage: Nullable<string>;

  status: TournamentStatus;
  currentRound: number;

  tier: Tier;
  featured?: boolean;

  location: Nullable<Location>;

  acceptsOnlineRegistration?: boolean;
  nicknamesAllowed?: boolean;
  visibility?: TournamentVisibility;

  inscriptionDateFrom?: Date;
  inscriptionDateTo?: Date;
  inscriptionFee?: number;
  inscriptionPrize?: number;

  preInscriptionDateFrom?: Date;
  preInscriptionDateTo?: Date;
  preInscriptionFee?: number;
  preInscriptionPrize?: string;

  isTeamTournament?: boolean;
  playersPerTeam?: number;
  formatPositionAId?: number;
  formatPositionBId?: number;
  formatPositionCId?: number;
  formatPositionDId?: number;
  formatPositionEId?: number;

  // Un torneo puede no tener fecha todavía: las dos columnas son
  // `DateTime?` en Prisma.
  dateFrom: Nullable<Date>;
  dateTo: Nullable<Date>;

  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;

  /** Quién lo creó. Nulo en los importados o anteriores a la columna. */
  createdByUserId?: Nullable<number>;
  createdBy?: Nullable<Pick<User, 'id' | 'name' | 'lastName'>>;

  players: TournamentPlayer[];
  matches: TournamentMatch[];
  tournamentTeams?: TournamentTeam[];
  tournamentTeamMatches?: TournamentTeamMatch[];
  results: TournamentResult[];
  organizer: Nullable<Pick<Organizer, 'id' | 'name' | 'store' | 'logoUrl'>>;
  organizers: TournamentOrganizerRelation[];
  format: Format;
  leagues: TournamentLeague[];
  topTournaments?: Tournament[];

  links: TournamentLink[];
  files: TournamentFile[];
  prizes: TournamentPrize[];
  roundPlannings?: TournamentRoundPlanning[];
  /** Las rondas del top de un suizo (`round` = la del top). */
  topRoundPlannings?: TournamentRoundPlanning[];
  judges?: TournamentJudge[];

  // Bounty: Leaders from previous round in a league with bounty rules
  bountyUserIds?: Nullable<number[]>;

  // Streaming: Featured match ID for overlay
  streamMatchId?: Nullable<number>;

  // Round Robin config
  legs?: Nullable<number>;
  bestOf?: Nullable<number>;
  revealAllRounds?: Nullable<boolean>;
  requireMatchProof?: Nullable<boolean>;
  maxPlayers?: Nullable<number>;
  enforceGameReporting?: Nullable<boolean>;
  // Si el torneo se transmite (lo confirma el organizador; apagado por defecto).
  hasStreaming?: Nullable<boolean>;
};

export type TournamentRoundPlanning = {
  id: number;
  tournamentId: number;
  round: number;
  startDate: string;
  endDate: string;
  actualStartDate: Nullable<string>;
  duration: Nullable<number>; // Duration in seconds
};

export type TournamentLeague = {
  id: number;
  leagueId: number;
  tournamentId: number;
  roundNumber: Nullable<number>;
  pointsMultiplier: Nullable<number>;
  league: League;
  tournament: Tournament;
};

export type TournamentMatch = {
  id: number;

  matchNumber: Nullable<number>;
  tableNumber: number;
  round: number;
  /** Mesa por el 3er puesto: va en la ronda de la final, fuera del bracket. */
  isThirdPlace?: boolean;
  /** Double elimination: llave, ronda en la llave y lugar en esa ronda. */
  bracketSide?: Nullable<BracketSide>;
  bracketRound?: Nullable<number>;
  bracketPosition?: Nullable<number>;
  replayUrl?: Nullable<string>;
  /** Estado del video del replay. Lo trae `getTournament` (folder, tournament-app). */
  replayVideo?: Nullable<ReplayVideo>;

  status: TournamentMatchStatus;

  player1: Nullable<
    Pick<User, 'id' | 'name' | 'lastName' | 'cossyId' | 'neuronId'> & {
      duelingBookId?: Nullable<string>;
    }
  >;
  player2: Nullable<
    Pick<User, 'id' | 'name' | 'lastName' | 'cossyId' | 'neuronId'> & {
      duelingBookId?: Nullable<string>;
    }
  >;
  winner: Nullable<
    Pick<User, 'id' | 'name' | 'lastName' | 'cossyId' | 'neuronId'>
  >;
  /**
   * Distingue empate de doble derrota. Nullable: los matches viejos, anteriores a
   * que el campo existiera, vienen en null y se tratan como empate.
   */
  resultType?: Nullable<MatchResultType>;
  /** Cuándo empezó a jugarse la mesa (lo marca el juez de un public). */
  startedAt?: Nullable<string>;
  /** Cuándo se cargó el resultado; null si sigue pendiente. */
  reportedAt?: Nullable<string>;
  /**
   * Quién cargó el resultado vigente. Null si está pendiente, si lo cargó un
   * sync (KCGN, Dueling Book, imports) o si es anterior a oct-2026.
   */
  reportedBy?: Nullable<Pick<User, 'id' | 'name' | 'lastName'>>;
  game1Winner?: Nullable<Pick<User, 'id'>>;
  game2Winner?: Nullable<Pick<User, 'id'>>;
  game3Winner?: Nullable<Pick<User, 'id'>>;
  game4Winner?: Nullable<Pick<User, 'id'>>;
  game5Winner?: Nullable<Pick<User, 'id'>>;
};

export type ReviewTournamentMatch = {
  id: number;

  tableNumber: number;
  round: number;

  status: TournamentMatchStatus;
  isPlayoff?: boolean;
  /**
   * El `Status` de la mesa en el archivo KTS (`ParseTournamentFile`). Sin él no
   * se distingue una doble derrota de una mesa sin reportar: las dos traen
   * `winner.user.id === -1`. No viene en los imports que no son de archivo.
   */
  ktsStatus?: 'Winner' | 'DoubleLoss' | 'Draw' | 'Pending';

  player1: Nullable<TournamentPlayer>;
  player2: Nullable<TournamentPlayer>;
  winner: Nullable<TournamentPlayer>;
};

export type TournamentResult = {
  id: number;
  player: TournamentPlayer;
  points: number;
  position: number;
  wins: number;
  losses: number;
  draws: number;
  userId: number;
  user: Pick<User, 'id' | 'name' | 'lastName' | 'cossyId'> &
    Partial<Pick<User, 'duelingBookId'>>;
  // Tiebreakers
  omwPercent: number;
  oomwPercent: number;
  lossSquaresSum: number;
  tiebreaker: string;
};

/** Valores comparables de un resultado, para la previsualización del recálculo. */
export type RecalculatedResultValues = Pick<
  TournamentResult,
  | 'position'
  | 'points'
  | 'wins'
  | 'losses'
  | 'draws'
  | 'omwPercent'
  | 'oomwPercent'
  | 'lossSquaresSum'
  | 'tiebreaker'
>;

export type RecalculatedResultRow = {
  playerId: number;
  userId: number;
  /** Lo guardado hoy. `null` si el jugador todavía no tiene resultado. */
  current: Nullable<RecalculatedResultValues>;
  /** Lo que quedaría al aplicar el recálculo. */
  next: RecalculatedResultValues;
  changed: boolean;
};

export type RecalculateResultsPreview = {
  tournamentId: number;
  changedCount: number;
  rows: RecalculatedResultRow[];
};

export type TournamentFixChange = {
  matchId: number;
  round: number;
  tableNumber: number;
  player1Name: string;
  player2Name: string;
  /** Cómo está cargado hoy. Ej: "BYE sin ganador (cuenta derrota)". */
  currentDescription: string;
  /** Cómo quedaría con este cambio. Ej: "BYE ganado". */
  proposedDescription: string;
};

/** Un jugador que se mueve al aplicar el escenario, medido contra el recálculo actual. */
export type TournamentFixEffect = {
  userId: number;
  playerName: string;
  baselineWins: number;
  baselineLosses: number;
  baselineDraws: number;
  baselinePosition: number;
  nextWins: number;
  nextLosses: number;
  nextDraws: number;
  nextPosition: number;
};

export type TournamentFixScenario = {
  /** Suma de |Δ| de récord contra los standings cargados. 0 = los reproduce. */
  distance: number;
  changes: TournamentFixChange[];
  effects: TournamentFixEffect[];
};

export type TournamentFixAnalysis = {
  /** Distancia de los matches tal como están hoy. 0 = no hay nada que resolver. */
  baselineDistance: number;
  exact: boolean;
  unique: boolean;
  /** La búsqueda se cortó por tamaño: la lista puede estar incompleta. */
  searchTruncated: boolean;
  scenarios: TournamentFixScenario[];
};

export type TournamentMatchInput = {
  tableNumber: number;
  player1: Nullable<number>;
  player2: Nullable<number>;
};

export enum TournamentMatchStatus {
  Pending = 'PENDING',
  Finished = 'FINISHED',
}

export enum MatchResultType {
  Win = 'WIN',
  Draw = 'DRAW',
  DoubleLoss = 'DOUBLE_LOSS',
}

export enum Region {
  Europe = 'eu',
  NorthAmerica = 'us',
  Oceania = 'oc',
  Japan = 'jp',
  World = 'world',
  AsianEnglish = 'ae',
  Korea = 'kr',
  Chinese = 'cn',
}

export type MetaFilters = {
  limit: number;
  page: number;
  search: string;
};

export enum Tier {
  Tier1 = 'TIER_1',
  Tier2 = 'TIER_2',
  Tier3 = 'TIER_3',
  Tier4 = 'TIER_4',
}

export enum TournamentLinkType {
  Facebook = 'Facebook',
  FacebookEvent = 'FacebookEvent',
  Phone = 'Phone',
  Email = 'Email',
  YouTube = 'YouTube',
  Instagram = 'Instagram',
  Twitch = 'Twitch',
  Kick = 'Kick',
  WhatsApp = 'WhatsApp',
  WhatsAppCommunity = 'WhatsAppCommunity',
  WhatsAppNumber = 'WhatsAppNumber',
  Website = 'Website',
  Custom = 'Custom',
  Twitter = 'Twitter',
  Discord = 'Discord',
  TikTok = 'TikTok',
  GoogleCalendar = 'GoogleCalendar',
  Neuron = 'Neuron',
}

export type Format = {
  id: number;
  name: string;
  slug: string;
};

export type TournamentLink = {
  id: number;
  label: string;
  type: TournamentLinkType;
  url: string;
};

export type TournamentFile = {
  id: number;
  tournamentId: number;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  createdAt: Date;
};

export enum TournamentPrizeType {
  Participation = 'PARTICIPATION',
  Swiss = 'SWISS',
  Top = 'TOP',
}

export type TournamentPrize = {
  id: number;
  prizeType: TournamentPrizeType;
  positionFrom: number;
  positionTo: number;
  prize: string;
  minPlayers: Nullable<number>;
  isCredit: boolean;
  creditAmount: Nullable<number>;
};

export type StoreTournament = {
  id: number;
  storeId: number;
  title: Nullable<string>;
  day: string;
  startTime: string;
  endTime: string;
  formatId: number;
  price: Nullable<number>;
  frequency: number;
  /** Canal de Discord que heredan sus torneos. Lo escribe un admin. */
  discordChannelId?: Nullable<string>;

  format: Format;
};

export enum CardRarity {
  SECRET_10000 = 'SECRET_10000',
  COLLECTOR = 'COLLECTOR',
  COMMON = 'COMMON',
  GHOST = 'GHOST',
  GOLD = 'GOLD',
  HOBBY = 'HOBBY',
  GHOST_GOLD = 'GHOST_GOLD',
  GRANDMASTER = 'GRANDMASTER',
  HOBBY_LEAGUE = 'HOBBY_LEAGUE',
  MOSAIC = 'MOSAIC',
  NORMAL_PARALLEL = 'NORMAL_PARALLEL',
  PLATINUM = 'PLATINUM',
  PRISMATIC_SECRET = 'PRISMATIC_SECRET',
  QUARTER_CENTURY = 'QUARTER_CENTURY',
  RARE = 'RARE',
  SECRET = 'SECRET',
  SHATTERFOIL = 'SHATTERFOIL',
  STARFOIL = 'STARFOIL',
  STARLIGHT = 'STARLIGHT',
  SUPER = 'SUPER',
  ULTIMATE = 'ULTIMATE',
  ULTRA = 'ULTRA',
  ULTRA_PHARAOH = 'ULTRA_PHARAOH',
  ULTRA_PARALLEL = 'ULTRA_PARALLEL',
  UNKNOWN = 'UNKNOWN',
}

export type Ranking = {
  points: Nullable<number>;
  user: Nullable<User>;
  totalTournaments: Nullable<number>;
  averageTournamentSize: Nullable<number>;
  reportedPoints: Nullable<number>;
  unreportedPoints: Nullable<number>;
  reportedTournaments: Nullable<number>;
  unreportedTournaments: Nullable<number>;
  metadata?: Record<string, unknown>;
};

export type WeeklyRankingEntry = {
  user: Nullable<User>;
  currentPosition: Nullable<number>;
  previousPosition: number | null;
  positionChange: Nullable<number>;
  currentPoints: Nullable<number>;
  previousPoints: Nullable<number>;
  pointsChange: Nullable<number>;
  totalTournaments: Nullable<number>;
  averageTournamentSize: Nullable<number>;
  reportedPoints: Nullable<number>;
  unreportedPoints: Nullable<number>;
  reportedTournaments: Nullable<number>;
  unreportedTournaments: Nullable<number>;
  isNew: Nullable<boolean>;
  metadata?: Record<string, unknown>;
};

export enum RankingSystem {
  ATP = 'ATP',
  ELO = 'ELO',
  FIFA = 'FIFA',
  HYBRID = 'HYBRID',
  LEAGUE = 'LEAGUE',
  TIME_WIZARD = 'TIME_WIZARD',
}

export type RankingPeriod = {
  id: number;
  name: string;
  slug: string;
  dateFrom: Date;
  dateTo: Date;
  formatId: Nullable<number>;
  organizerId: Nullable<number>;
  system: RankingSystem;
  isActive: boolean;
  isFrozen: boolean;
  createdAt: Date;
  updatedAt: Date;
  format?: Format;
  organizer?: Organizer;
};

export type RankingSnapshot = {
  id: number;
  userId: number;
  periodId: number;
  position: number;
  points: number;
  totalTournaments: number;
  averageTournamentSize: number;
  reportedPoints: number;
  unreportedPoints: number;
  reportedTournaments: number;
  unreportedTournaments: number;
  metadata?: Record<string, unknown>;
  user: User;
  period: RankingPeriod;
};

/** La lista que rige un torneo, en lo mínimo para nombrarla. */
export type TournamentBanlist = {
  id: number;
  name: string;
  formatId: number;
  logoUrl: string | null;
  shareHash: string | null;
  /** `null` = lista global (Konami / retro): no tiene página pública. */
  organizerSlug: string | null;
};

export type Banlist = {
  id: number;
  formatId: number;
  name: string;
  effectiveDate: Date;
  /** `null` = rige hasta que salga la siguiente. */
  expirationDate: Nullable<Date>;
  isActive: boolean;
  cards: BanlistCard[];
  archetypes: BanlistArchetype[];
  /** Cartas y patrones que esta lista considera exclusivamente genéricos. */
  generics: BanlistGeneric[];
  /** Patrones de nombre prohibidos. */
  patterns: BanlistCardPattern[];
  format: Format;
  tags: Nullable<string[]>;
  /** Criterio de orden: 'NAME', 'TYPE' o null (orden de carga). */
  cardSort: string | null;
  /** Hash del link público, o null si no se compartió. */
  shareHash: string | null;
  /** `null` = lista global (las de Konami); con dueño, la de ese organizador. */
  organizerId: number | null;
  /** Key en S3 del logo de la lista, o null. Se sube por REST. */
  logoUrl: string | null;
  /**
   * Lista a la que ésta sucede, elegida a mano. `null` = la sucesión se infiere
   * por (formato, dueño, fecha efectiva).
   */
  previousBanlistId: number | null;
  organizer: Nullable<Pick<Organizer, 'id' | 'name' | 'logoUrl' | 'slug'>>;
  /**
   * Los formatos que la tienen como lista fija: marzo 2010 es de Advanced y
   * rige siempre en Edison. Vacío = no es la fija de ninguno.
   */
  formats: Pick<Format, 'id' | 'name'>[];
};

/**
 * Los tres niveles de restricción de un arquetipo. Sin `UNLIMITED`: la
 * ausencia de fila ya significa "sin restricción", igual que en `BanlistCard`.
 */
export enum BanlistArchetypeStatus {
  FORBIDDEN = 'FORBIDDEN',
  LIMITED = 'LIMITED',
  SEMI_LIMITED = 'SEMI_LIMITED',
}

/** Un arquetipo restringido, en alguno de los tres niveles de `BanlistArchetypeStatus`. */
export type BanlistArchetype = {
  id: number;
  archetypeId: number;
  archetype: { id: number; name: string; type: string };
  status: BanlistArchetypeStatus;
  representativeKonamiId: Nullable<number>;
  representativeCard: Nullable<Pick<Card, 'konamiId' | 'name' | 'image'>>;
};

/**
 * Una carta o un patrón que la lista considera EXCLUSIVAMENTE genérico: no
 * cuenta como parte de ningún arquetipo. La lista de cartas prohibidas le
 * sigue aplicando. Una fila trae `konamiId` O `namePattern`, nunca los dos.
 */
export type BanlistGeneric = {
  id: number;
  konamiId: Nullable<number>;
  namePattern: Nullable<string>;
  card: Nullable<Pick<Card, 'konamiId' | 'name' | 'image'>>;
};

/** Un patrón de nombre prohibido: cubre toda carta cuyo nombre lo incluya. */
export type BanlistCardPattern = {
  id: number;
  namePattern: string;
  status: BanlistCardStatus;
  conditional: boolean;
};

export enum RulingType {
  MONSTER_EFFECT = 'MONSTER_EFFECT',
  SPELL_EFFECT = 'SPELL_EFFECT',
  TRAP_EFFECT = 'TRAP_EFFECT',
  CHAIN_INTERACTION = 'CHAIN_INTERACTION',
  TIMING = 'TIMING',
  PRIORITY = 'PRIORITY',
  DECK_CONSTRUCTION = 'DECK_CONSTRUCTION',
  OTHER = 'OTHER',
}

export type RulingExample = {
  id: number;
  text: string;
  cards: Pick<Card, 'id' | 'konamiId' | 'name' | 'image'>[];
};

export type RulingSource = {
  id: number;
  url: string;
  label: Nullable<string>;
};

export type Ruling = {
  id: number;
  title: string;
  description: string;
  type: RulingType;
  isActive: boolean;

  cards: Pick<Card, 'id' | 'konamiId' | 'name' | 'image'>[];
  examples: RulingExample[];
  sources: RulingSource[];

  createdById: Nullable<number>;
  createdBy?: Pick<User, 'id' | 'name' | 'lastName'> | null;
  updatedById: Nullable<number>;
  updatedBy?: Pick<User, 'id' | 'name' | 'lastName'> | null;

  createdAt: Date;
  updatedAt: Date;
};

export enum BanlistCardStatus {
  FORBIDDEN = 'FORBIDDEN',
  LIMITED = 'LIMITED',
  SEMI_LIMITED = 'SEMI_LIMITED',
  UNLIMITED = 'UNLIMITED',
}

export type BanlistCard = {
  id: number;
  konamiId: number;
  status: BanlistCardStatus;
  /** `null` en las listas escritas a mano: no hay lista anterior. */
  previousStatus: Nullable<BanlistCardStatus>;
  /** La carta sólo está prohibida si el mazo usa un arquetipo de la lista de restricciones. */
  conditional: boolean;
  card: Pick<Card, 'konamiId' | 'name' | 'frameType' | 'image'>;
};

/**
 * En qué dirección se movió una carta de una lista a la otra.
 *
 * `ADDED` y `FREED` son los bordes de `STRICTER` y `LOOSER` —entrar a la lista
 * es endurecerse desde `UNLIMITED`— y van aparte porque es la distinción que
 * mira el jugador: lo que ahora no puede jugar y lo que le devolvieron.
 */
export enum BanlistDiffMovement {
  ADDED = 'ADDED',
  STRICTER = 'STRICTER',
  LOOSER = 'LOOSER',
  FREED = 'FREED',
}

export type BanlistDiffCard = {
  konamiId: number;
  name: string;
  image: Nullable<string>;
  frameType: Nullable<string>;
  /** El estado en la lista base. `UNLIMITED` = no estaba. */
  from: BanlistCardStatus;
  /** El estado en la lista nueva. `UNLIMITED` = salió de la lista. */
  to: BanlistCardStatus;
  movement: BanlistDiffMovement;
};

export type BanlistDiffArchetype = {
  archetypeId: number;
  name: string;
  representativeKonamiId: Nullable<number>;
  representativeImage: Nullable<string>;
};

/** Una lista identificada: el encabezado del diff y las opciones del selector. */
export type BanlistDiffRef = {
  id: number;
  name: string;
  /**
   * El formato al que pertenece. Lo pide `publicBanlistHistory` —su selector
   * mezcla formatos y "Mayo 2026" no dice de cuál es—; el diff no lo pide,
   * así que ahí llega `undefined`.
   */
  formatId?: number;
  effectiveDate: Date;
  /** `null` = lista global (las de Konami). */
  organizerName: Nullable<string>;
};

export type BanlistDiff = {
  banlist: BanlistDiffRef;
  /** `null` = es la primera lista de su scope, no hay contra qué compararla. */
  base: Nullable<BanlistDiffRef>;
  cards: BanlistDiffCard[];
  archetypesAdded: BanlistDiffArchetype[];
  archetypesRemoved: BanlistDiffArchetype[];
  options: BanlistDiffRef[];
};

export type ParsedTeamMemberData = {
  id: number;
  cossyId: string;
  name: string;
  lastName: string;
  teamPosition: number;
};

export type ParsedTeamData = {
  ktsId: string;
  name: string;
  members: ParsedTeamMemberData[];
  rank: number;
  wins: number;
  points: number;
  dropRound: number;
  dropReason: string;
};

export type ParsedTeamMatchData = {
  round: number;
  tableNumber: number;
  team1KtsId: string;
  team2KtsId: string;
  winnerKtsId: Nullable<string>;
  team1: Nullable<ParsedTeamData>;
  team2: Nullable<ParsedTeamData>;
  winner: Nullable<ParsedTeamData>;
};

export type ParsedTeamResultData = {
  teamKtsId: string;
  teamName: string;
  position: number;
  points: number;
  wins: number;
  draws: number;
  losses: number;
  members: ParsedTeamMemberData[];
};

export type ParsedTournament = Pick<
  Tournament,
  'title' | 'dateFrom' | 'currentRound' | 'rounds' | 'konamiId'
> & {
  withTop?: number;
  hasTopTournament?: boolean;
  topTournamentId?: number;
  // Team tournament fields
  isTeamTournament?: boolean;
  teams?: ParsedTeamData[];
  teamMatches?: ParsedTeamMatchData[];
  teamResults?: ParsedTeamResultData[];
  players: (Partial<TournamentPlayer> & {
    // Nombre crudo del archivo KTS: `user` ya viene pisado por el de la DB
    // cuando el Konami ID matchea.
    ktsName?: string;
    ktsLastName?: string;
    matchMethod?: string;
    topdeckName?: string;
    topdeckUserId?: string;
    limitlessUsername?: string;
    limitlessName?: string;
    kcgnName?: string;
    kcgnCossyId?: string;
    kcgnDeck?: KcgnSnapshotDeck;
  })[];
  matches: Partial<ReviewTournamentMatch>[];
  playoffMatches?: Partial<ReviewTournamentMatch>[];
  playoffPlayers?: Partial<TournamentPlayer>[];
  playoffResults?: Partial<TournamentResult>[];
  results: Partial<TournamentResult>[];
};

export type LeagueRuleConfig = {
  id: number;
  leagueRuleId: number;
  rounds: string;
  multiplier: number;
  excludeTopN: number;
};

export type LeaguePositionPoints = {
  minPlayers: number;
  position: number;
  points: number;
};

export type LeagueRules = {
  id: number;
  leagueId: number;
  // El SDK ya define `enum LeagueRule` y el schema GraphQL declara
  // `$rule: LeagueRule!` en las mutations, pero acá el campo venía como
  // `string`: el propio SDK ensanchaba su enum de vuelta, y cualquier
  // comparación contra una regla inexistente compilaba (YGO-225 / YGO-285).
  rule: LeagueRule;
  createdAt: Date;
  description: Nullable<string>;
  configs?: LeagueRuleConfig[];
  positionPoints?: LeaguePositionPoints[];
};

export type League = {
  id: number;
  name: string;
  slug: Nullable<string>;
  organizerId: number;

  gameTypeId: number;
  /** El formato principal (el del playoff). */
  formatId: number;
  /** Todos los formatos que suman a la liga, el principal primero. */
  formatIds: number[];

  description: string;
  ogImage: Nullable<string>;

  dateFrom: Date;
  dateTo: Date;

  numberOfDates: number;
  type: LeagueType;
  status: Nullable<LeagueStatus>;

  hasExtraPayment: boolean;
  extraPaymentAmount: number;

  createdAt: Date;
  updatedAt: Date;
  deletedAt: Nullable<Date>;

  organizer: Organizer;
  tournaments: TournamentLeague[];
  players: LeaguePlayer[];
  rankings: LeagueRanking[];
  rules: LeagueRules[];
};

export type LeaguePlayer = {
  id: number;
  leagueId: number;
  userId: number;
  banned: boolean;

  createdAt: Date;
  updatedAt: Date;
  deletedAt: Nullable<Date>;

  league: League;
  user: User;
  payments: LeaguePayment[];
};

export type LeaguePayment = {
  id: number;
  leaguePlayerId: number;
  tournamentId: number;
  paidAt: Date;
  createdAt: Date;

  leaguePlayer: LeaguePlayer;
  tournament: Tournament;
};

export type LeagueRanking = {
  id: number;
  leagueId: number;
  userId: number;

  position: number;
  points: number;
  wins: Nullable<number>;
  losses: Nullable<number>;
  draws: Nullable<number>;
  winAverage: Nullable<number>;
  tournamentsPlayed: Nullable<number>;
  tops: Nullable<number>;
  tournamentWins: Nullable<number>;

  createdAt: Date;
  updatedAt: Date;
  deletedAt: Nullable<Date>;

  user: User;
  league: League;
};

// Head to Head

export type HeadToHeadStats = {
  opponent: User;
  wins: number;
  losses: number;
  draws: number;
  total: number;
};

export type HeadToHeadMatch = {
  id: number;
  tournamentId: number;
  tournamentName: string;
  round: number;
  table?: number | null;
  result: 'WIN' | 'LOSS' | 'DRAW';
  player1Score?: number | null;
  player2Score?: number | null;
  date: string;
};

export type HeadToHeadFilters = {
  resultsValidated?: boolean;
};

export type HeadToHeadMatchesFilters = {
  opponentId: number;
  resultsValidated?: boolean;
  limit?: number;
};

export type UserSearch = Pick<
  User,
  | 'id'
  | 'name'
  | 'secondName'
  | 'lastName'
  | 'secondLastName'
  | 'email'
  | 'cossyId'
  | 'masterDuelId'
  | 'duelLinksId'
  | 'cossyValidated'
  | 'homeStoreId'
>;

export type TournamentPlayerData = {
  playerName: string;
  playerCossyId: string;
  recordedWins: number;
  recordedLosses: number;
  recordedDraws: number;
  actualWins: number;
  actualLosses: number;
  actualDraws: number;
  actualPoints: number;
  winsDiff: number;
  hasDiscrepancy: boolean;
  recordedPoints: number;
  lossesDiff: number;
  drawsDiff: number;
  pointsDiff: number;
};

// Community Teams (clubs / crews — distinct from TournamentTeam)

export type TeamMemberPosition =
  | 'CONTENT_CREATOR'
  | 'COACH'
  | 'JUDGE'
  | 'EXPERIENCED_PLAYER'
  | 'BEGINNER_PLAYER';

export type TeamInvitationStatus =
  | 'PENDING'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';

export type TeamLink = {
  id: number;
  type: StoreLinkType;
  url: string;
  label?: string | null;
};

export type TeamMember = {
  id: number;
  teamId: number;
  position: TeamMemberPosition;
  joinedAt: Date | string;
  user: Pick<User, 'id' | 'name' | 'lastName' | 'picture'>;
  team?: Team;
  createdAt?: Date | string;
  updatedAt?: Date | string;
};

export type TeamInvitation = {
  id: number;
  team: Pick<Team, 'id' | 'name' | 'slug' | 'logoUrl'>;
  invitedUser: Pick<User, 'id' | 'name' | 'lastName' | 'picture'>;
  invitedBy: Pick<User, 'id' | 'name' | 'lastName' | 'picture'>;
  proposedPosition: TeamMemberPosition;
  status: TeamInvitationStatus;
  message?: string | null;
  createdAt: Date | string;
  respondedAt?: Date | string | null;
};

export type Team = {
  id: number;
  name: string;
  slug: string;
  logoUrl?: string | null;
  description?: string | null;
  isPublic: boolean;
  owner: Pick<User, 'id' | 'name' | 'lastName' | 'picture'>;
  members: TeamMember[];
  invitations?: TeamInvitation[];
  links: TeamLink[];
  gameTypes: Pick<GameType, 'id' | 'name'>[];
  formats: Pick<Format, 'id' | 'name'>[];
  membersCount: number;
  createdAt: Date | string;
  updatedAt: Date | string;
};

// Recordings (LP tracking offline para postproducción)

export type MatchEventType =
  | 'LP_CHANGE'
  | 'TURN_CHANGE'
  | 'PHASE_CHANGE'
  | 'RECORDING_START'
  | 'RECORDING_END'
  | 'DUEL_END'
  | 'NOTE';

export type MatchEventPayload = Generic | null;

export type MatchEventInput = {
  eventType: MatchEventType;
  timestampMs: string;
  recordingId?: number | null;
  playerId?: number | null;
  payload?: MatchEventPayload;
};

export type Recording = {
  id: number;
  matchId: Nullable<number>;
  startedAtMs: string;
  endedAtMs?: string | null;
  notes?: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
};

export type MatchEvent = {
  id: number;
  matchId: Nullable<number>;
  recordingId?: number | null;
  timestampMs: string;
  serverReceivedAtMs: string;
  eventType: MatchEventType;
  payload?: MatchEventPayload;
  playerId?: number | null;
  createdAt: Date | string;
};

export type RecordingTimelinePlayer = {
  id?: number | null;
  name: string;
  lastName?: string | null;
  fullName: string;
  nickname?: string | null;
  deck?: string | null;
  score: string;
  gameWins: number[];
  startingLp: number;
};

export type RecordingTimelineMatch = {
  id: number;
  round: number;
  tableNumber: number;
};

export type RecordingTimelineEvent = {
  t: number;
  type: MatchEventType;
  playerId?: number | null;
  payload?: MatchEventPayload;
};

export type RecordingTimeline = {
  recordingId: number;
  durationMs: number;
  match: RecordingTimelineMatch;
  players: RecordingTimelinePlayer[];
  events: RecordingTimelineEvent[];
};

// KTS Vision (emparejamientos por transcripción de imagen)

export type KtsVisionSeat = {
  cardGameId: string | null;
  name: string | null;
};

export type KtsVisionRow = {
  table: number | null;
  left: KtsVisionSeat;
  right: KtsVisionSeat;
};

export type KtsRoundRead = {
  rows: KtsVisionRow[];
  notes: string[];
};

export type KtsStandingsRow = {
  rank: number | null;
  cardGameId: string | null;
  name: string | null;
  wins: number | null;
  losses: number | null;
  draws: number | null;
  tiebreaker: string | null;
};

export type KtsStandingsRead = {
  rows: KtsStandingsRow[];
  notes: string[];
};

/**
 * Espejo de `VisionMatchWinRow` de `@ygoargentina/tournament-system`, declarado
 * a mano como los de arriba porque el SDK no depende del paquete. Los
 * porcentajes van como texto: la tabla los imprime con coma decimal y cuatro
 * decimales.
 */
export type VisionMatchWinRow = {
  rank: number | null;
  playerNumber: number | null;
  name: string | null;
  points: number | null;
  omwPercent: string | null;
  gwPercent: string | null;
  ogwPercent: string | null;
  played: number | null;
  wins: number | null;
  draws: number | null;
  byes: number | null;
};

export type MatchWinStandingsRead = {
  rows: VisionMatchWinRow[];
  notes: string[];
};

export enum BanlistPredictionStatus {
  OPEN = 'OPEN',
  SCORED = 'SCORED',
}

export type BanlistPredictionMark =
  | 'EXACT'
  | 'DIRECTION'
  | 'WRONG'
  | 'UNPREDICTED';

export type BanlistPredictionCard = {
  konamiId: number;
  name: string;
  image?: Nullable<string>;
  frameType?: Nullable<string>;
  status: BanlistCardStatus;
};

export type BanlistPrediction = {
  id: number;
  status: BanlistPredictionStatus;
  shareHash: string;
  baseBanlistId: number;
  lastEditedAt: string;
  cards: BanlistPredictionCard[];
};

export type BanlistPredictionAuthor = {
  id: number;
  name: string;
  lastName?: Nullable<string>;
  picture?: Nullable<string>;
};

export type BanlistPredictionRef = { id: number; name: string };

/** La lista contra la que se arma una predicción (la última global, sin ilimitadas). */
export type BanlistPredictionBase = {
  id: number;
  name: string;
  effectiveDate: string;
  cards: BanlistPredictionCard[];
};

export type BanlistPredictionCardResult = {
  konamiId: number;
  name: string;
  image?: Nullable<string>;
  frameType?: Nullable<string>;
  from: BanlistCardStatus;
  predicted: BanlistCardStatus;
  actual: BanlistCardStatus;
  mark: BanlistPredictionMark;
  points: number;
};

export type BanlistPredictionResult = {
  score: number;
  exactHits: number;
  directionHits: number;
  wrong: number;
  unpredicted: number;
  cards: BanlistPredictionCardResult[];
};

export type PublicBanlistPrediction = {
  id: number;
  status: BanlistPredictionStatus;
  shareHash: string;
  lastEditedAt: string;
  user: BanlistPredictionAuthor;
  base: Nullable<BanlistPredictionRef>;
  target: Nullable<BanlistPredictionRef>;
  cards: BanlistPredictionCard[];
  changes: { cards: BanlistDiffCard[] };
  result: Nullable<BanlistPredictionResult>;
  position: Nullable<number>;
};

export type BanlistPredictionSummary = {
  id: number;
  shareHash: string;
  score: Nullable<number>;
  exactHits: Nullable<number>;
  lastEditedAt: string;
  targetBanlist: Nullable<BanlistPredictionRef>;
};

export type BanlistPredictionLeaderboard = {
  target: Nullable<BanlistPredictionRef>;
  rounds: { id: number; name: string; entries: number }[];
  entries: {
    position: number;
    shareHash: string;
    score: number;
    exactHits: number;
    lastEditedAt: string;
    user: BanlistPredictionAuthor;
  }[];
  total: number;
};

export type BanlistPredictionCardInput = {
  konamiId: number;
  status: BanlistCardStatus;
};
